import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createTemporaryOverride,
  deleteTemporaryOverride,
  extractApiError,
  getTeacherOverview,
  getTemporaryOverrides,
} from './apiService';
import { darkPanelSx, insetPanelSx, sectionCardSx } from './designSystem';

function formatTemporaryOverrideExpiry(expiresAt) {
  const expiresMs = Date.parse(expiresAt || '');
  if (!Number.isFinite(expiresMs)) {
    return 'Expires soon';
  }

  const diffMs = expiresMs - Date.now();
  if (diffMs <= 0) {
    return 'Expired';
  }

  const diffMinutes = Math.round(diffMs / (60 * 1000));
  if (diffMinutes < 60) {
    return `Expires in ${diffMinutes} min`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `Expires in ${diffHours} hr`;
  }

  const diffDays = Math.round(diffHours / 24);
  return `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
}

function formatDateTime(value) {
  const timestamp = Date.parse(value || '');
  if (!Number.isFinite(timestamp)) {
    return 'Not seen yet';
  }
  return new Date(timestamp).toLocaleString();
}

function formatEventUrl(event) {
  const currentUrl = (event?.current_url || '').trim();
  if (!currentUrl) {
    return event?.domain || 'Unknown page';
  }

  try {
    const url = new URL(currentUrl);
    const host = url.hostname.replace(/^www\./, '');
    const path = `${url.pathname || ''}${url.search || ''}`.replace(/\/$/, '');
    const label = `${host}${path && path !== '/' ? path : ''}`;
    return label.length > 78 ? `${label.slice(0, 75)}...` : label;
  } catch (error) {
    return currentUrl.length > 78 ? `${currentUrl.slice(0, 75)}...` : currentUrl;
  }
}

function getProfileId(profile) {
  return profile?._id || profile?.id || '';
}

function getStudentLabel(student) {
  return student?.student_name || student?.student_email || student?.student_id || student?.label || 'Unnamed student';
}

function getStatusStyle(status) {
  if (status === 'active') {
    return { bgcolor: 'rgba(19,148,127,0.12)', color: '#0C6E5E' };
  }
  if (status === 'pending') {
    return { bgcolor: 'rgba(223,162,58,0.16)', color: '#926212' };
  }
  return { bgcolor: 'rgba(82,103,122,0.12)', color: '#52677A' };
}

function TeacherConsole() {
  const [overview, setOverview] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [temporaryOverrides, setTemporaryOverrides] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [newDurationMinutes, setNewDurationMinutes] = useState(90);
  const [newNote, setNewNote] = useState('');
  const [studentQuery, setStudentQuery] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  const profiles = useMemo(() => overview?.profiles || [], [overview]);
  const students = useMemo(() => overview?.students || [], [overview]);
  const activeSchedules = useMemo(() => overview?.active_schedules || [], [overview]);
  const staffOverrideEnabled = Boolean(overview?.staff_override_enabled);

  const selectedProfileName = useMemo(() => {
    return profiles.find((profile) => getProfileId(profile) === selectedProfileId)?.profile_name || 'Classroom profile';
  }, [profiles, selectedProfileId]);

  const classOptions = useMemo(() => {
    const values = new Set();
    students.forEach((student) => {
      [student.group_name, ...(student.class_names || [])].forEach((value) => {
        if (value) {
          values.add(value);
        }
      });
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [students]);

  const filteredStudents = useMemo(() => {
    const query = studentQuery.trim().toLowerCase();
    return students.filter((student) => {
      const classValues = [student.group_name, ...(student.class_names || [])].filter(Boolean);
      const matchesClass = !classFilter || classValues.includes(classFilter);
      const searchable = [
        student.label,
        student.student_name,
        student.student_email,
        student.student_id,
        student.device_name,
        student.assigned_profile_name,
        ...classValues,
      ].filter(Boolean).join(' ').toLowerCase();
      return matchesClass && (!query || searchable.includes(query));
    });
  }, [classFilter, studentQuery, students]);

  const summary = overview?.summary || {};
  const needsAttention = (summary.pending_devices || 0) + (summary.stale_devices || 0);

  useEffect(() => {
    const loadTeacherConsole = async () => {
      try {
        setLoading(true);
        setStatusMessage(null);
        const data = await getTeacherOverview();
        setOverview(data);

        const defaultProfileId =
          data?.active_schedules?.[0]?.profile_id ||
          getProfileId(data?.profiles?.[0]) ||
          '';
        setSelectedProfileId(defaultProfileId);
      } catch (error) {
        setStatusMessage({ severity: 'error', text: extractApiError(error, 'Unable to load the teacher console right now.') });
      } finally {
        setLoading(false);
      }
    };

    loadTeacherConsole();
  }, []);

  useEffect(() => {
    if (!selectedProfileId) {
      setTemporaryOverrides([]);
      return;
    }

    getTemporaryOverrides(selectedProfileId)
      .then((data) => setTemporaryOverrides(data?.overrides || []))
      .catch((error) => {
        setTemporaryOverrides([]);
        setStatusMessage({ severity: 'error', text: extractApiError(error, 'Unable to load quick allows for this classroom.') });
      });
  }, [selectedProfileId]);

  const handleQuickAllow = async (event) => {
    event.preventDefault();
    if (!selectedProfileId || !newUrl.trim()) {
      return;
    }

    try {
      const response = await createTemporaryOverride(
        {
          url: newUrl.trim(),
          expires_in_minutes: Number(newDurationMinutes) || 90,
          note: newNote.trim() || undefined,
        },
        selectedProfileId
      );
      setTemporaryOverrides((current) => [...current, response.override]);
      setNewUrl('');
      setNewDurationMinutes(90);
      setNewNote('');
      setStatusMessage({ severity: 'success', text: `Quick allow added for ${selectedProfileName}.` });
    } catch (error) {
      setStatusMessage({ severity: 'error', text: extractApiError(error, 'Unable to add that quick allow right now.') });
    }
  };

  const handleDeleteOverride = async (overrideId) => {
    try {
      await deleteTemporaryOverride(overrideId);
      setTemporaryOverrides((current) => current.filter((item) => item.id !== overrideId));
      setStatusMessage({ severity: 'success', text: 'Quick allow removed.' });
    } catch (error) {
      setStatusMessage({ severity: 'error', text: extractApiError(error, 'Unable to remove that quick allow right now.') });
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: { xs: 4, md: 5 } }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Card sx={darkPanelSx}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Chip
                  label="Teacher Console"
                  sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'white', fontWeight: 700 }}
                />
                <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>
                  Classroom controls for today.
                </Typography>
                <Typography sx={{ color: '#d7e4ea', lineHeight: 1.7, maxWidth: 700 }}>
                  Watch your roster, see recent filtering decisions, and add short classroom-only allows when a lesson page gets caught.
                </Typography>
              </Box>
              <Stack spacing={1} sx={{ minWidth: { md: 260 } }}>
                <Chip label={overview?.organization?.name || 'School workspace'} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }} />
                <Chip label={activeSchedules.length ? `${activeSchedules.length} class schedule active` : 'No class schedule active'} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {statusMessage && <Alert severity={statusMessage.severity}>{statusMessage.text}</Alert>}

        {!staffOverrideEnabled && (
          <Alert severity="warning">
            Quick allows are not enabled for staff in this workspace. You can still monitor students here; an admin can enable staff override in the main dashboard.
          </Alert>
        )}

        <Grid container spacing={2}>
          {[
            { label: 'Students', value: summary.students_total || 0, helper: 'Visible to this teacher' },
            { label: 'Active devices', value: summary.active_devices || 0, helper: 'Currently enrolled' },
            { label: 'Needs attention', value: needsAttention, helper: 'Pending or stale devices' },
            { label: 'Quick allows', value: temporaryOverrides.length, helper: selectedProfileId ? selectedProfileName : 'No profile selected' },
          ].map((stat) => (
            <Grid item xs={12} sm={6} md={3} key={stat.label}>
              <Card sx={sectionCardSx}>
                <CardContent sx={{ p: 2.25 }}>
                  <Typography sx={{ color: '#52677A', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {stat.label}
                  </Typography>
                  <Typography sx={{ color: '#0B2E43', fontWeight: 850, fontSize: '2rem', mt: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: '#52677A', fontSize: '0.84rem', mt: 0.35 }}>
                    {stat.helper}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2.5} alignItems="stretch">
          <Grid item xs={12} lg={7}>
            <Card sx={{ ...sectionCardSx, height: '100%' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: '#0B2E43' }}>
                      Students
                    </Typography>
                    <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                      Read-only roster status. Enrollment edits stay with admins.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label="Find student"
                      value={studentQuery}
                      onChange={(event) => setStudentQuery(event.target.value)}
                      size="small"
                    />
                    <TextField
                      select
                      label="Class"
                      value={classFilter}
                      onChange={(event) => setClassFilter(event.target.value)}
                      size="small"
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="">All classes</MenuItem>
                      {classOptions.map((className) => (
                        <MenuItem key={className} value={className}>{className}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Stack>

                <Stack spacing={1.25}>
                  {filteredStudents.length === 0 && (
                    <Box sx={{ ...insetPanelSx, p: 2 }}>
                      <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                        No students matched
                      </Typography>
                      <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                        This usually means roster rows do not include this teacher name/email yet, or the current filter is too narrow.
                      </Typography>
                    </Box>
                  )}
                  {filteredStudents.slice(0, 12).map((student) => {
                    const statusStyle = getStatusStyle(student.status);
                    const classText = [student.group_name, ...(student.class_names || [])].filter(Boolean).join(' • ');
                    return (
                      <Box key={student.id} sx={{ ...insetPanelSx, p: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography sx={{ color: '#0B2E43', fontWeight: 800 }}>
                                {getStudentLabel(student)}
                              </Typography>
                              <Chip size="small" label={student.status || 'unknown'} sx={{ ...statusStyle, fontWeight: 800, textTransform: 'capitalize' }} />
                            </Stack>
                            <Typography sx={{ color: '#52677A', mt: 0.5, fontSize: '0.86rem' }}>
                              {student.assigned_profile_name || 'District baseline'}{classText ? ` • ${classText}` : ''}
                            </Typography>
                            <Typography sx={{ color: '#52677A', mt: 0.35, fontSize: '0.86rem' }}>
                              Last seen: {formatDateTime(student.last_seen_at)}
                            </Typography>
                          </Box>
                          <Typography sx={{ color: '#7A8FA0', fontSize: '0.82rem', flexShrink: 0 }}>
                            {student.device_name || student.label || 'Managed device'}
                          </Typography>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Stack spacing={2.5}>
              <Card sx={sectionCardSx}>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                    Quick allow
                  </Typography>
                  <Typography sx={{ color: '#52677A', lineHeight: 1.7, mb: 2 }}>
                    Pick the classroom profile, paste the blocked URL, and allow it temporarily.
                  </Typography>

                  <form onSubmit={handleQuickAllow}>
                    <Stack spacing={1.5}>
                      <TextField
                        select
                        label="Classroom profile"
                        value={selectedProfileId}
                        onChange={(event) => setSelectedProfileId(event.target.value)}
                        fullWidth
                        disabled={profiles.length === 0}
                      >
                        {profiles.map((profile) => (
                          <MenuItem key={getProfileId(profile)} value={getProfileId(profile)}>
                            {profile.profile_name}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Blocked URL or domain"
                        value={newUrl}
                        onChange={(event) => setNewUrl(event.target.value)}
                        disabled={!staffOverrideEnabled || !selectedProfileId}
                        fullWidth
                      />
                      <TextField
                        select
                        label="Duration"
                        value={newDurationMinutes}
                        onChange={(event) => setNewDurationMinutes(Number(event.target.value))}
                        disabled={!staffOverrideEnabled || !selectedProfileId}
                        fullWidth
                      >
                        <MenuItem value={15}>15 minutes</MenuItem>
                        <MenuItem value={60}>1 hour</MenuItem>
                        <MenuItem value={90}>Class period</MenuItem>
                        <MenuItem value={480}>School day</MenuItem>
                      </TextField>
                      <TextField
                        label="Optional note"
                        value={newNote}
                        onChange={(event) => setNewNote(event.target.value)}
                        disabled={!staffOverrideEnabled || !selectedProfileId}
                        fullWidth
                      />
                      <Button type="submit" variant="contained" disabled={!staffOverrideEnabled || !selectedProfileId}>
                        Allow Now
                      </Button>
                    </Stack>
                  </form>
                </CardContent>
              </Card>

              <Card sx={sectionCardSx}>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                    Active quick allows
                  </Typography>
                  <Stack spacing={1.25}>
                    {temporaryOverrides.length === 0 && (
                      <Typography sx={{ color: '#7A8FA0' }}>
                        No active quick allows for {selectedProfileId ? selectedProfileName : 'this classroom'}.
                      </Typography>
                    )}
                    {temporaryOverrides.map((override) => (
                      <Box key={override.id} sx={{ ...insetPanelSx, p: 1.75 }}>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 750, wordBreak: 'break-word' }}>
                          {override.url}
                        </Typography>
                        <Typography sx={{ color: '#52677A', mt: 0.5, fontSize: '0.86rem' }}>
                          {formatTemporaryOverrideExpiry(override.expires_at)}
                          {override.note ? ` • ${override.note}` : ''}
                        </Typography>
                        <Button
                          color="error"
                          size="small"
                          onClick={() => handleDeleteOverride(override.id)}
                          disabled={!staffOverrideEnabled}
                          sx={{ mt: 0.75 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1.5 }}>
                  Active classes
                </Typography>
                <Stack spacing={1.25}>
                  {activeSchedules.length === 0 && (
                    <Typography sx={{ color: '#7A8FA0' }}>
                      No scheduled classroom profile is active right now.
                    </Typography>
                  )}
                  {activeSchedules.map((entry) => {
                    const schedule = entry.schedule || {};
                    return (
                      <Box key={`${entry.profile_id}-${schedule.id || schedule.start_time}`} sx={{ ...insetPanelSx, p: 1.75 }}>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 750 }}>
                          {schedule.class_name || entry.profile_name || 'Classroom'}
                        </Typography>
                        <Typography sx={{ color: '#52677A', mt: 0.45, fontSize: '0.86rem' }}>
                          {entry.profile_name} • {schedule.start_time || '--:--'}-{schedule.end_time || '--:--'}
                          {schedule.room ? ` • ${schedule.room}` : ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1.5 }}>
                  Recent student decisions
                </Typography>
                <Stack spacing={1.25}>
                  {(overview?.recent_events || []).length === 0 && (
                    <Typography sx={{ color: '#7A8FA0' }}>
                      No recent filtering decisions are visible for this roster yet.
                    </Typography>
                  )}
                  {(overview?.recent_events || []).slice(0, 8).map((event) => {
                    const isBlock = String(event.decision).toLowerCase() === 'block';
                    return (
                      <Box key={event.id} sx={{ ...insetPanelSx, p: 1.75, borderLeft: `3px solid ${isBlock ? '#C04531' : '#13947F'}` }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            label={isBlock ? 'Blocked' : 'Allowed'}
                            sx={{
                              bgcolor: isBlock ? 'rgba(192,69,49,0.12)' : 'rgba(19,148,127,0.12)',
                              color: isBlock ? '#9A3322' : '#0C6E5E',
                              fontWeight: 800,
                            }}
                          />
                          <Typography sx={{ color: '#0B2E43', fontWeight: 750, wordBreak: 'break-word' }}>
                            {formatEventUrl(event)}
                          </Typography>
                        </Stack>
                        <Typography sx={{ color: '#52677A', mt: 0.55, fontSize: '0.86rem' }}>
                          {event.student_name || event.student_email || event.device_label || 'Student'} • {formatDateTime(event.created_at)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}

export default TeacherConsole;
